import api from './api';

export interface ReportFilter {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'between' | 'in' | 'not_in';
  value: any;
}

export interface ReportConfiguration {
  name: string;
  description?: string;
  dataSource: 'members' | 'transactions' | 'classes' | 'bookings' | 'events' | 'custom';
  fields: string[];
  filters: ReportFilter[];
  groupBy?: string[];
  aggregations?: {
    field: string;
    operation: 'count' | 'sum' | 'avg' | 'min' | 'max';
  }[];
  sortBy?: {
    field: string;
    direction: 'ASC' | 'DESC';
  }[];
  chartType: 'bar' | 'line' | 'pie' | 'doughnut' | 'table' | 'card';
  dateRange?: {
    type: 'current' | 'custom';
    startDate?: Date;
    endDate?: Date;
    period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  };
}

export interface SavedReport {
  id: string;
  name: string;
  description?: string;
  createdBy: string;
  configuration: ReportConfiguration;
  isShared: boolean;
  isTemplate: boolean;
  visibility: 'private' | 'shared' | 'public';
  createdAt: string;
  updatedAt: string;
}

class ReportAPI {
  // Dashboard overview
  async getDashboardOverview() {
    const response = await api.get('/reports/dashboard/overview');
    return response.data;
  }

  // Coaching report
  async getCoachingReport(params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get(
      `/reports/coaching?${queryParams.toString()}`
    );
    return response.data;
  }

  // Member statistics
  async getMemberStats() {
    const response = await api.get('/reports/members/stats');
    return response.data;
  }

  // Transaction statistics
  async getTransactionStats(params?: {
    startDate?: string;
    endDate?: string;
    type?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.type) queryParams.append('type', params.type);

    const response = await api.get(
      `/reports/transactions/stats?${queryParams.toString()}`
    );
    return response.data;
  }

  // Transaction drill-down (individual transactions behind an Accounting report category)
  async getTransactionDrillDown(params: {
    startDate: string;
    endDate: string;
    source: string;
    transactionType?: string;
    tierName?: string;
    amenityType?: string;
    amenityName?: string;
    /** fnb_sales / shop_sales: one payment method (helcim / cash / member_account) */
    paymentMethod?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    queryParams.append('source', params.source);
    if (params.transactionType) queryParams.append('transactionType', params.transactionType);
    if (params.tierName) queryParams.append('tierName', params.tierName);
    if (params.amenityType) queryParams.append('amenityType', params.amenityType);
    if (params.amenityName) queryParams.append('amenityName', params.amenityName);
    if (params.paymentMethod) queryParams.append('paymentMethod', params.paymentMethod);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

    const response = await api.get(
      `/reports/transactions/drill-down?${queryParams.toString()}`
    );
    return response.data;
  }

  // Revenue report
  async getRevenueReport(params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month' | 'quarter';
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);

    const response = await api.get(
      `/reports/revenue?${queryParams.toString()}`
    );
    return response.data;
  }

  // Class statistics
  async getClassStats(params?: {
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(
      `/api/reports/classes/stats?${queryParams.toString()}`
    );
    return response.data;
  }

  // Custom reports
  async createCustomReport(configuration: ReportConfiguration) {
    const response = await api.post(
      '/reports/custom',
      configuration
    );
    return response.data;
  }

  // Export report
  async exportReport(configuration: ReportConfiguration, format: 'csv' | 'json' = 'csv') {
    const response = await api.post(
      `/reports/export?format=${format}`,
      configuration,
      { 
        responseType: format === 'csv' ? 'blob' : 'json'
      }
    );
    return response.data;
  }

  // Saved reports
  async saveReport(report: {
    name: string;
    description?: string;
    configuration: ReportConfiguration;
    isShared?: boolean;
    visibility?: 'private' | 'shared' | 'public';
  }) {
    const response = await api.post(
      '/reports/save',
      report
    );
    return response.data;
  }

  async getSavedReports(): Promise<SavedReport[]> {
    const response = await api.get('/reports/saved');
    return response.data;
  }

  async executeReport(reportId: string) {
    const response = await api.get(
      `/reports/saved/${reportId}/execute`
    );
    return response.data;
  }

  async deleteReport(reportId: string) {
    await api.delete(`/reports/saved/${reportId}`);
  }

  // Court utilization report
  async getCourtUtilizationReport(params: {
    startDate: string;
    endDate: string;
    groupBy?: 'day' | 'week' | 'month';
    amenityType?: string;
    bookingSource?: 'all' | 'amenity' | 'coaching' | 'group_play';
  }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params.amenityType) queryParams.append('amenityType', params.amenityType);
    if (params.bookingSource) queryParams.append('bookingSource', params.bookingSource);

    const response = await api.get(`/reports/court-utilization?${queryParams.toString()}`);
    return response.data;
  }

  // Member utilization report
  async getMemberUtilizationReport(params: {
    startDate: string;
    endDate: string;
    rankBy?: 'visits' | 'bookings' | 'hours';
    activitySource?: 'all' | 'classes' | 'amenity' | 'coaching' | 'group_play' | 'guest_play';
    amenityType?: string;
    topN?: number;
  }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.rankBy) queryParams.append('rankBy', params.rankBy);
    if (params.activitySource) queryParams.append('activitySource', params.activitySource);
    if (params.amenityType) queryParams.append('amenityType', params.amenityType);
    if (params.topN !== undefined) queryParams.append('topN', String(params.topN));

    const response = await api.get(`/reports/member-utilization?${queryParams.toString()}`);
    return response.data;
  }

  // F&B financial report (goods sold / tax / tips over a date range)
  async getFnBFinancialReport(params: { startDate: string; endDate: string }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);

    const response = await api.get(`/fnb/reports/financial?${queryParams.toString()}`);
    return response.data;
  }

  // Single F&B order (itemized breakdown) — used by the financial report's order drill-down modal
  async getFnBOrder(orderId: string) {
    const response = await api.get(`/fnb/orders/${orderId}`);
    return response.data;
  }

  // F&B item sales report (quantity sold / revenue per menu item over a date range)
  async getFnBItemSalesReport(params: { startDate: string; endDate: string }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);

    const response = await api.get(`/fnb/reports/items?${queryParams.toString()}`);
    return response.data;
  }

  // Shop POS financial report (goods sold / tax / payment method breakdown over a date range)
  async getShopFinancialReport(params: { startDate: string; endDate: string }) {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);

    const response = await api.get(`/shop/reports/financial?${queryParams.toString()}`);
    return response.data;
  }

  // Single Shop order (itemized breakdown) — used by the financial report's order drill-down modal
  async getShopOrder(orderId: string) {
    const response = await api.get(`/shop/orders/${orderId}`);
    return response.data;
  }

  // Report access tracking
  async logReportAccess(reportId: string) {
    try {
      const response = await api.post('/reports/log-access', { reportId });
      return response.data;
    } catch (err) {
      console.error('Error logging report access:', err);
      // Don't throw - this is non-critical
      return null;
    }
  }

  async getReportAccessHistory() {
    try {
      const response = await api.get('/reports/access-history');
      return response.data;
    } catch (err) {
      console.error('Error fetching report access history:', err);
      return { data: [] };
    }
  }

  // Helper method to download exported data
  downloadData(data: Blob | any, filename: string, format: 'csv' | 'json') {
    if (format === 'csv' && data instanceof Blob) {
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } else {
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }
}

export const reportAPI = new ReportAPI();
